import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider, LinearProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined, ConfirmationNumberOutlined,
    EmojiEventsOutlined, AddOutlined,
} from '@mui/icons-material';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined';
import StarOutlined from '@mui/icons-material/StarOutlined';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    gold:    '#F5C842',
    orange:  '#FF9500',
};

const GRAND_PRIZES = [
    { label: 'iPhone 16',       sublabel: '256GB Pro Max',       icon: SmartphoneOutlined, color: '#5AC8FA', glow: '#5AC8FA', emoji: '📱' },
    { label: 'Generator',       sublabel: '3.5KVA Honda',        icon: BoltOutlined,       color: B.orange,  glow: B.orange,  emoji: '⚡' },
    { label: 'Toyota Corolla',  sublabel: '2024 Model',          icon: DirectionsCarOutlined, color: B.gold, glow: B.gold,   emoji: '🚗' },
];

const MINOR_PRIZES = [
    { label: '₦50,000 Cash',   tickets: 1,  color: B.green  },
    { label: '₦20,000 Cash',   tickets: 3,  color: '#5AC8FA' },
    { label: '₦10,000 Cash',   tickets: 8,  color: B.purple  },
    { label: 'Data Bundle 10GB', tickets: 15, color: B.orange  },
];

const HOW_TO_EARN = [
    { label: 'Save ₦10,000+',          tickets: '+2 tickets', icon: '💰' },
    { label: 'Improve credit score',    tickets: '+3 tickets', icon: '📈' },
    { label: 'Refer a friend',          tickets: '+1 ticket',  icon: '👥' },
    { label: 'Pay bills on time',       tickets: '+1 ticket',  icon: '⚡' },
    { label: 'Daily check-in (7-day)',  tickets: '+1 ticket',  icon: '🔥' },
];

const Raffle: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [tickets,   setTickets]   = useState(12);
    const [entered,   setEntered]   = useState(false);
    const [animating, setAnimating] = useState(false);

    const maxTickets   = 50;
    const drawDate     = '31st December 2025';
    const totalEntries = 1847;

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleEnter = () => {
        if (entered) return;
        setAnimating(true);
        setTimeout(() => { setAnimating(false); setEntered(true); }, 1000);
    };

    const winChance = ((tickets / totalEntries) * 100).toFixed(2);

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: 'linear-gradient(145deg, #7A4800 0%, #CC7A00 60%, #F5C842 100%)',
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#fff', .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold, .3)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate('/rewards')}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .2)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .55), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>Rewards</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Raffle Draw
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .6, background: alpha('#000', .2), border: `1px solid ${alpha('#fff', .2)}`, borderRadius: 20, px: 1.5, py: .5, backdropFilter: 'blur(10px)' }}>
                        <ConfirmationNumberOutlined sx={{ color: '#fff', fontSize: 14 }} />
                        <Typography sx={{ color: '#fff', fontSize: '.72rem', fontWeight: 700 }}>{tickets} Tickets</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Ticket + entry card ── */}
                <Box sx={{
                    ...glassCard, p: 2.5, mb: 2,
                    background: isDark
                        ? `linear-gradient(145deg, ${alpha('#7A4800', .4)}, ${alpha(B.mid, .7)})`
                        : `linear-gradient(145deg, ${alpha(B.gold, .06)}, #fff)`,
                    border: `1px solid ${alpha(B.gold, isDark ? .25 : .15)}`,
                }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
                        <Box>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: .5 }}>Your Tickets</Typography>
                            <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '2.6rem', fontWeight: 700, lineHeight: 1 }}>
                                {tickets}
                            </Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.72rem', mt: .25 }}>of {maxTickets} max</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: .5 }}>Win Chance</Typography>
                            <Typography sx={{ color: B.gold, fontFamily: "'Instrument Serif', serif", fontSize: '1.6rem', fontWeight: 700 }}>{winChance}%</Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>{totalEntries.toLocaleString()} entries</Typography>
                        </Box>
                    </Box>

                    {/* Ticket progress */}
                    <Typography sx={{ color: textFaint, fontSize: '.65rem', fontWeight: 600, mb: 1 }}>Ticket progress</Typography>
                    <LinearProgress variant="determinate" value={(tickets / maxTickets) * 100}
                                    sx={{ height: 8, borderRadius: 4, background: alpha(B.gold, .15), '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${B.orange}, ${B.gold})`, borderRadius: 4 } }} />
                    <Box display="flex" justifyContent="space-between" mt={.75}>
                        <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>0</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>{maxTickets} max</Typography>
                    </Box>

                    <Button fullWidth onClick={handleEnter} disabled={entered || animating}
                            sx={{
                                mt: 2.5, py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                background: entered
                                    ? alpha(B.green, .15)
                                    : `linear-gradient(135deg, ${B.orange}, ${B.gold})`,
                                color: entered ? B.green : '#1A0F00',
                                boxShadow: entered ? 'none' : `0 12px 32px ${alpha(B.gold, .35)}`,
                                border: entered ? `1.5px solid ${alpha(B.green, .3)}` : 'none',
                                '&:disabled': { opacity: entered ? 1 : .5 },
                            }}>
                        {animating ? 'Entering draw…' : entered ? '✓ Entered in Draw' : `Enter Draw with ${tickets} Tickets`}
                    </Button>

                    <Typography sx={{ color: textFaint, fontSize: '.7rem', textAlign: 'center', mt: 1.5 }}>
                        Draw date: <span style={{ color: textMid, fontWeight: 600 }}>{drawDate}</span>
                    </Typography>
                </Box>

                {/* ── Grand Prizes ── */}
                <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <EmojiEventsOutlined sx={{ color: B.gold, fontSize: 18 }} />
                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em' }}>Grand Prizes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {GRAND_PRIZES.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <Box key={p.label} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.75, borderRadius: '16px', background: alpha(p.glow, isDark ? .1 : .05), border: `1px solid ${alpha(p.glow, .2)}` }}>
                                    <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: alpha(p.color, .15), border: `1px solid ${alpha(p.color, .25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: '1.6rem' }}>{p.emoji}</Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem' }}>{p.label}</Typography>
                                        <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>{p.sublabel}</Typography>
                                    </Box>
                                    <Box sx={{ background: alpha(p.color, .12), border: `1px solid ${alpha(p.color, .25)}`, borderRadius: '8px', px: 1, py: .4 }}>
                                        <Typography sx={{ color: p.color, fontSize: '.6rem', fontWeight: 700, letterSpacing: '.04em' }}>#{i + 1} PRIZE</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Minor prizes */}
                    <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mt: 2.5, mb: 1.5 }}>Also Up for Grabs</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                        {MINOR_PRIZES.map(p => (
                            <Box key={p.label} sx={{ p: 1.25, borderRadius: '12px', background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: p.color, boxShadow: `0 0 6px ${p.color}`, flexShrink: 0 }} />
                                <Box>
                                    <Typography sx={{ color: textPri, fontSize: '.75rem', fontWeight: 600 }}>{p.label}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.62rem' }}>{p.tickets} winners</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── How to earn tickets ── */}
                <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddOutlined sx={{ color: B.gold, fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>Earn More Tickets</Typography>
                    </Box>
                    {HOW_TO_EARN.map((item, i) => (
                        <React.Fragment key={item.label}>
                            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: alpha(B.gold, .1), border: `1px solid ${alpha(B.gold, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: '1rem' }}>{item.icon}</Typography>
                                </Box>
                                <Typography sx={{ color: textPri, fontSize: '.83rem', fontWeight: 500, flex: 1 }}>{item.label}</Typography>
                                <Box sx={{ background: alpha(B.gold, .12), border: `1px solid ${alpha(B.gold, .25)}`, borderRadius: '8px', px: 1, py: .35 }}>
                                    <Typography sx={{ color: B.gold, fontSize: '.68rem', fontWeight: 700 }}>{item.tickets}</Typography>
                                </Box>
                            </Box>
                            {i < HOW_TO_EARN.length - 1 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                        </React.Fragment>
                    ))}
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Raffle;